import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  XCircle,
  Loader2,
  X,
  FileText,
  Filter,
  Users,
  AlertCircle,
  Info
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../services/api';

interface ComandaFisica {
  id: number;
  numero: string;
  nome: string;
  descricao?: string;
  ativo: boolean;
  uso_atual: number;
  created_at: string;
  updated_at: string;
}

const ComandasFisicasPage: React.FC = () => {
  const [comandas, setComandas] = useState<ComandaFisica[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState<string>('todos');
  const [showModal, setShowModal] = useState(false);
  const [editingComanda, setEditingComanda] = useState<ComandaFisica | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<number | null>(null);
  const [toggling, setToggling] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    numero: '',
    nome: '',
    descricao: ''
  });

  // Carregar comandas físicas
  const loadComandas = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filtroAtivo !== 'todos') {
        params.append('ativo', filtroAtivo);
      }
      
      if (busca) {
        params.append('busca', busca);
      }

      const response = await api.get(`/api/comandas-fisicas?${params}`);
      setComandas(response.data);
    } catch (error) {
      console.error('Erro ao carregar comandas físicas:', error);
      toast.error('Erro ao carregar comandas físicas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadComandas();
  }, [filtroAtivo, busca]);

  // Filtrar comandas
  const comandasFiltradas = comandas.filter(comanda => {
    const matchBusca = comanda.numero.toLowerCase().includes(busca.toLowerCase()) ||
                      comanda.nome.toLowerCase().includes(busca.toLowerCase());
    
    if (filtroAtivo === 'todos') return matchBusca;
    if (filtroAtivo === 'ativas') return matchBusca && comanda.ativo;
    if (filtroAtivo === 'inativas') return matchBusca && !comanda.ativo;
    
    return matchBusca;
  });

  // Abrir modal para nova comanda
  const openModal = (comanda?: ComandaFisica) => {
    if (comanda) {
      setEditingComanda(comanda);
      setFormData({
        numero: comanda.numero,
        nome: comanda.nome,
        descricao: comanda.descricao || ''
      });
    } else {
      setEditingComanda(null);
      setFormData({
        numero: '',
        nome: '',
        descricao: ''
      });
    }
    setShowModal(true);
  };

  // Fechar modal
  const closeModal = () => {
    setShowModal(false);
    setEditingComanda(null);
    setFormData({
      numero: '',
      nome: '',
      descricao: ''
    });
  };

  // Salvar comanda
  const saveComanda = async () => {
    if (!formData.numero.trim() || !formData.nome.trim()) {
      toast.error('Número e nome são obrigatórios');
      return;
    }

    setSaving(true);
    try {
      if (editingComanda) {
        await api.put(`/api/comandas-fisicas/${editingComanda.id}`, formData);
        toast.success('Comanda atualizada com sucesso!');
      } else {
        await api.post('/api/comandas-fisicas', formData);
        toast.success('Comanda criada com sucesso!');
      }
      
      closeModal();
      loadComandas();
    } catch (error: any) {
      console.error('Erro ao salvar comanda:', error);
      toast.error(error.response?.data?.erro || 'Erro ao salvar comanda');
    } finally {
      setSaving(false);
    }
  };

  // Excluir comanda
  const deleteComanda = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir esta comanda?')) return;

    setDeleting(id);
    try {
      await api.delete(`/api/comandas-fisicas/${id}`);
      toast.success('Comanda excluída com sucesso!');
      loadComandas();
    } catch (error: any) {
      console.error('Erro ao excluir comanda:', error);
      toast.error(error.response?.data?.erro || 'Erro ao excluir comanda');
    } finally {
      setDeleting(null);
    }
  };

  // Toggle status ativo
  const toggleStatus = async (id: number, ativo: boolean) => {
    setToggling(id);
    try {
      await api.put(`/api/comandas-fisicas/${id}`, { ativo: !ativo });
      toast.success(ativo ? 'Comanda desativada!' : 'Comanda ativada!');
      loadComandas();
    } catch (error: any) {
      console.error('Erro ao alterar status:', error);
      toast.error(error.response?.data?.erro || 'Erro ao alterar status');
    } finally {
      setToggling(null);
    }
  };

  // Estatísticas
  const stats = {
    total: comandas.length,
    ativas: comandas.filter(c => c.ativo).length,
    inativas: comandas.filter(c => !c.ativo).length,
    emUso: comandas.filter(c => c.uso_atual > 0).length,
    disponiveis: comandas.filter(c => c.ativo && c.uso_atual === 0).length
  };

  return (
    <div className="space-y-3 md:space-y-4 lg:space-y-6 px-2 md:px-4 lg:px-0">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 md:gap-3">
              <FileText className="h-6 w-6 md:h-7 md:w-7 lg:h-8 lg:w-8 text-orange-600" />
              Comandas Físicas
            </h1>
            <p className="text-xs md:text-sm lg:text-base text-gray-600 dark:text-gray-400 mt-1">
              Gerencie as comandas físicas pré-cadastradas para uso em mesas
            </p>
          </div>
          <button
            onClick={() => openModal()}
            className="flex items-center justify-center gap-2 px-3 py-2 md:px-4 md:py-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-lg hover:from-orange-600 hover:to-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all transform hover:scale-105 text-sm md:text-base font-medium w-full md:w-auto"
          >
            <Plus className="h-4 w-4 md:h-5 md:w-5" />
            Nova Comanda
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3 lg:gap-4">
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <FileText className="h-4 w-4 md:h-5 md:w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Total</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Ativas</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.ativas}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 md:h-5 md:w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Em Uso</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.emUso}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Disponíveis</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.disponiveis}</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 col-span-2 md:col-span-1">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-8 h-8 md:w-10 md:h-10 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
              <XCircle className="h-4 w-4 md:h-5 md:w-5 text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Inativas</p>
              <p className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white">{stats.inativas}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros e Busca */}
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-3 md:p-4 lg:p-5">
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <Filter className="h-4 w-4 md:h-5 md:w-5 text-gray-600 dark:text-gray-400" />
          <h3 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 dark:text-white">
            Filtros
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {/* Busca */}
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por número ou nome..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white transition-all"
              />
            </div>
          </div>

          {/* Filtro Status */}
          <div>
            <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
              Status
            </label>
            <select
              value={filtroAtivo}
              onChange={(e) => setFiltroAtivo(e.target.value)}
              className="w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white transition-all"
            >
              <option value="todos">Todas</option>
              <option value="ativas">Ativas</option>
              <option value="inativas">Inativas</option>
            </select>
          </div>
        </div>
      </div>

      {/* Lista de Comandas */}
      {loading ? (
        <div className="flex justify-center items-center py-12 md:py-16 lg:py-20">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : comandasFiltradas.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4 lg:gap-6">
          {comandasFiltradas.map((comanda) => (
            <div
              key={comanda.id}
              className={`bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-5 lg:p-6 border-2 transition-all duration-300 hover:shadow-lg group ${
                comanda.ativo 
                  ? 'border-green-300 dark:border-green-700 bg-green-50/50 dark:bg-green-900/10' 
                  : 'border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50'
              }`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3 md:mb-4">
                <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    comanda.ativo 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-400 dark:bg-gray-600 text-white'
                  }`}>
                    <FileText className="h-5 w-5 md:h-6 md:w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white truncate">
                      {comanda.nome}
                    </h3>
                    <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                      #{comanda.numero}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => toggleStatus(comanda.id, comanda.ativo)}
                  disabled={toggling === comanda.id}
                  className={`p-1.5 md:p-2 rounded-lg transition-colors flex-shrink-0 ${
                    comanda.ativo 
                      ? 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30' 
                      : 'text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                  } disabled:opacity-50`}
                  title={comanda.ativo ? 'Desativar' : 'Ativar'}
                >
                  {toggling === comanda.id ? (
                    <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                  ) : comanda.ativo ? (
                    <Eye className="h-4 w-4 md:h-5 md:w-5" />
                  ) : (
                    <EyeOff className="h-4 w-4 md:h-5 md:w-5" />
                  )}
                </button>
              </div>

              {/* Status e Uso */}
              <div className="space-y-2 md:space-y-3 mb-3 md:mb-4">
                <div className={`inline-flex items-center gap-1.5 md:gap-2 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full text-xs md:text-sm font-medium ${
                  comanda.ativo 
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                }`}>
                  {comanda.ativo ? (
                    <CheckCircle className="h-3 w-3 md:h-4 md:w-4" />
                  ) : (
                    <XCircle className="h-3 w-3 md:h-4 md:w-4" />
                  )}
                  {comanda.ativo ? 'Ativa' : 'Inativa'}
                </div>
                <div className="flex items-center gap-2 text-xs md:text-sm">
                  <Users className={`h-3.5 w-3.5 md:h-4 md:w-4 ${
                    comanda.uso_atual > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-gray-400'
                  }`} />
                  <span className={`font-medium ${
                    comanda.uso_atual > 0 
                      ? 'text-orange-600 dark:text-orange-400' 
                      : 'text-gray-600 dark:text-gray-400'
                  }`}>
                    {comanda.uso_atual > 0 
                      ? `${comanda.uso_atual} mesa(s) em uso` 
                      : 'Disponível'
                    }
                  </span>
                </div>
              </div>

              {/* Descrição */}
              {comanda.descricao && (
                <div className="mb-3 md:mb-4 p-2 md:p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {comanda.descricao}
                  </p>
                </div>
              )}

              {/* Ações */}
              <div className="flex gap-2">
                <button
                  onClick={() => openModal(comanda)}
                  className="flex-1 flex items-center justify-center gap-1.5 md:gap-2 bg-blue-600 text-white py-2 md:py-2.5 rounded-lg font-medium hover:bg-blue-700 transition-colors text-xs md:text-sm"
                >
                  <Edit className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  Editar
                </button>
                <button
                  onClick={() => deleteComanda(comanda.id)}
                  disabled={comanda.uso_atual > 0 || deleting === comanda.id}
                  className="p-2 md:p-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title={comanda.uso_atual > 0 ? 'Não é possível excluir comanda em uso' : 'Excluir'}
                >
                  {deleting === comanda.id ? (
                    <Loader2 className="h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg text-center py-8 md:py-12 px-4">
          <FileText className="h-12 w-12 md:h-16 md:w-16 mx-auto mb-4 text-gray-400 opacity-50" />
          <h3 className="text-base md:text-lg lg:text-xl font-medium text-gray-900 dark:text-white mb-2">
            Nenhuma comanda encontrada
          </h3>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
            {busca || filtroAtivo !== 'todos' 
              ? 'Tente ajustar os filtros de busca'
              : 'Crie sua primeira comanda física'
            }
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 md:p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md border-2 border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="p-4 md:p-5 lg:p-6">
              <div className="flex justify-between items-start mb-4 md:mb-6">
                <h2 className="text-base md:text-lg lg:text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                  <FileText className="h-5 w-5 text-orange-600" />
                  {editingComanda ? 'Editar Comanda' : 'Nova Comanda'}
                </h2>
                <button
                  onClick={closeModal}
                  className="p-1.5 md:p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-4 w-4 md:h-5 md:w-5" />
                </button>
              </div>

              <div className="space-y-3 md:space-y-4">
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Número <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    className="w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white transition-all"
                    placeholder="Ex: 001, 002, A01..."
                    autoFocus
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Nome <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    className="w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white transition-all"
                    placeholder="Ex: Comanda 001"
                  />
                </div>

                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 md:mb-2">
                    Descrição <span className="text-gray-500 text-xs">(Opcional)</span>
                  </label>
                  <textarea
                    value={formData.descricao}
                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                    className="w-full px-3 py-2 text-sm md:text-base border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-700 dark:text-white transition-all resize-none"
                    placeholder="Descrição opcional..."
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-2 md:gap-3 mt-4 md:mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 py-2 md:py-2.5 rounded-lg font-medium hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm md:text-base"
                >
                  Cancelar
                </button>
                <button
                  onClick={saveComanda}
                  disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 md:py-2.5 rounded-lg font-medium hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-sm md:text-base"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      {editingComanda ? 'Atualizar' : 'Criar'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informações */}
      <div className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border-2 border-orange-200 dark:border-orange-800 rounded-lg p-4 md:p-5 lg:p-6">
        <div className="flex items-start gap-3 md:gap-4">
          <div className="w-10 h-10 md:w-12 md:h-12 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
            <Info className="h-5 w-5 md:h-6 md:w-6 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm md:text-base lg:text-lg font-semibold text-orange-900 dark:text-orange-100 mb-2">
              Como funcionam as Comandas Físicas?
            </h4>
            <ul className="text-xs md:text-sm text-orange-800 dark:text-orange-200 space-y-1.5 md:space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 font-bold mt-0.5">•</span>
                <span>Comandas físicas são pré-cadastradas e podem ser associadas a mesas pelos garçons</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 font-bold mt-0.5">•</span>
                <span>Quando uma comanda está em uso, ela não pode ser excluída até ser liberada</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 font-bold mt-0.5">•</span>
                <span>Comandas inativas não aparecem para os garçons associarem às mesas</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 font-bold mt-0.5">•</span>
                <span>O campo "Em uso" mostra quantas mesas estão usando cada comanda no momento</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComandasFisicasPage;
